/** The ledger — the transaction of a record edit, always visible in edit mode (plan
 *  §1.4). No modal review sheet: the ledger IS the review. Top to bottom:
 *
 *  1. Header — "Draft · N changes", Undo / Redo (tooltips name the change), Close.
 *  2. Restech banner — who feeds the record and how many changes its next update undoes
 *     (`config.managedNote` says what the feed rewrites — an order's "Ventrata overwrites
 *     20 order fields"); the info variant while feed updates are paused, or a queued
 *     action previews the pause — in the server's words (`config.suspensionOf`), never a
 *     pause it has not stated.
 *  3. Changes grouped by entity, with Discard all (undoable).
 *  4. Choices needed — blocking Save until answered; defaults only where the catalog has
 *     one (cancel mode, freeze email, update events).
 *  5. On save — the debounced preview, or the instant catalog estimate while it loads.
 *  6. Description (optional). A required one (`config.descriptionMinLength`, a reason the
 *     save must carry) sits in the foot above Save instead, so it is in view under every
 *     segment of a segmented dock, and Save waits for it.
 *  7. "Save N changes" (⌘S). Guest-visible events, external writes or a cancellation in
 *     the plan open a one-line confirmation on the button (↵ confirms); otherwise it
 *     saves at once. Disabled — with the reason in words — while a change is stale or
 *     invalid or a choice is missing.
 *  8. After save — a receipt; the record re-seeds from the response, the editor stays
 *     open. Only the changes the save sent leave the draft: an edit made while it was in
 *     flight stays, against the value just stored (then the draft is dirty again and the
 *     receipt gives way to it) — on its real id when the save created its row (`created`).
 *
 *  409 → the drifted entries turn stale (Keep mine / Take theirs); 400 → errors on their
 *  fields plus a summary; a refused save (`refusalFailure`) → the server's reason with where
 *  the change is made instead, no Try again, the draft kept. Close editor (Esc): clean exits, dirty asks inline (Save ·
 *  Discard · Keep editing).
 *
 *  `variant="sheet"` (content narrower than 1100 px, `LedgerDock`) collapses to a peek
 *  bar "Draft · 4 changes · Save". The ledger is a complementary landmark, never a
 *  dialog — the record shortcuts must keep working inside it. While mounted it binds
 *  ⌘Z / ⇧⌘Z / ⌘S / Esc (`shortcuts={false}` when the page binds them itself).
 *
 *  `tabs` / `body` make it a segmented dock (the tour's "Preview | Changes N"): `tabs`
 *  sits under the header, and a `body` replaces the ledger's own content (2–6) while it
 *  is set. The header, the Save foot and the shortcuts stay mounted under every segment,
 *  so switching segments never loses the save state. `config.description === false`
 *  drops the Description section (a save that cannot carry one).
 *
 *  `mode="view"` is that dock in view mode (the tour's Preview, D6): the header shows
 *  `viewTitle` with no undo / redo / close (the room they take is kept), no shortcut is
 *  bound, there is no Save foot, and `body` is all it shows. It is the same element in
 *  both modes — pressing E re-renders it in place, so whatever `body` holds stays mounted
 *  and nothing in the dock moves.
 */
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RedoIcon from '@mui/icons-material/Redo';
import SyncIcon from '@mui/icons-material/Sync';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import UndoIcon from '@mui/icons-material/Undo';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  IconButton,
  Link,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { helpStoryPath } from '@/help/paths';
import { formatZoned } from '@/utils/zonedTime';

import { actionPath, compileChangeset, freshFromDrift, recordKey, type SavedChange } from '../draft/changeSet';
import {
  useDraft,
  useDraftGroups,
  useDraftSummary,
  useDraftUndoRedo,
} from '../draft/DraftProvider';
import { useRecordShortcuts } from '../draft/useRecordShortcuts';
import {
  asDriftFailure,
  asRefusalFailure,
  asValidationFailure,
  failureMessage,
  type RefusalLink,
} from '../editor/errors';
import { useRecordDescription, useRecordEditor } from '../editor/RecordEditorProvider';
import type { RecordApplyResult, RecordPreview } from '../editor/types';
import { ChoiceQuestion } from '../fields/ChoiceQuestion';

import { LedgerChanges } from './LedgerChanges';
import { LedgerOnSave } from './LedgerOnSave';
import { LedgerReceipt } from './LedgerReceipt';
import { catalogEstimate, confirmItems, ledgerGroups, saveBlock } from './model';

export type LedgerVariant = 'side' | 'sheet';

export interface LedgerProps {
  variant?: LedgerVariant;
  /** Bind ⌘Z / ⇧⌘Z / ⌘S / Esc while mounted (default true). */
  shortcuts?: boolean;
  /** Under the header: a segment switch (the tour dock's "Preview | Changes N"). */
  tabs?: ReactNode;
  /** In place of the ledger's own content while set (another segment); the header, the
   *  Save foot and the shortcuts stay. */
  body?: ReactNode;
  /** `view`: the dock in view mode — see above. Default `edit`. */
  mode?: 'edit' | 'view';
  /** View mode: the header's title and the landmark's name (default "Preview"). */
  viewTitle?: string;
  /** The restored-draft banner offers Discard (default) — false where the page already
   *  offers one beside it (a header's Discard, the ledger's Discard all): Keep alone, and the
   *  banner keeps to one line. */
  restoredDiscard?: boolean;
}

type Failure =
  | { kind: 'drift'; count: number; gone: number }
  | { kind: 'validation'; message: string }
  | { kind: 'refused'; message: string; links: RefusalLink[] }
  | { kind: 'error'; message: string };

type Phase = 'idle' | 'checking' | 'confirm';

const NO_MEMBERSHIPS: ReadonlySet<string> = new Set();

function changesText(n: number): string {
  return n === 0 ? 'no changes' : `${n} ${n === 1 ? 'change' : 'changes'}`;
}

function Kbd({ children }: { children: ReactNode }) {
  return (
    <Box
      component="kbd"
      sx={{
        ml: 0.75,
        px: 0.5,
        border: 1,
        borderColor: 'currentColor',
        borderRadius: 0.5,
        fontFamily: 'inherit',
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.4,
        opacity: 0.75,
      }}
    >
      {children}
    </Box>
  );
}

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  const id = useId();
  return (
    <Box component="section" aria-labelledby={id} sx={{ display: 'grid', gap: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 24 }}>
        <Typography id={id} variant="caption" component="h3" color="text.secondary" sx={{ fontWeight: 600, m: 0, flex: 1 }}>
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </Box>
  );
}

const DENSE_ALERT_SX = {
  py: 0,
  px: 1.25,
  alignItems: 'flex-start',
  '& .MuiAlert-icon': { py: 0.75, mr: 1, fontSize: 18 },
  '& .MuiAlert-message': { typography: 'caption', py: 0.75 },
} as const;

export function Ledger({
  variant = 'side',
  shortcuts = true,
  tabs,
  body: segmentBody,
  mode = 'edit',
  viewTitle = 'Preview',
  restoredDiscard = true,
}: LedgerProps) {
  const viewOnly = mode === 'view';
  const editor = useRecordEditor();
  const {
    config,
    labelFormatter,
    preview,
    raised,
    body,
    formErrors,
    errorsFor,
    validateValue,
    invalidInputs,
    fieldForPath,
    focusField,
    closePrompt,
    requestClose,
    cancelClose,
    exitEdit,
    resetSession,
    setApplyFailure,
    receipt,
    setReceipt,
  } = editor;
  const { state, dispatch, restored, dismissRestored, discardRestored, persistence } = useDraft();
  const { count } = useDraftSummary();
  const draftGroups = useDraftGroups(config.recordOrder ?? []);
  const history = useDraftUndoRedo(labelFormatter);
  const { description, setDescription } = useRecordDescription();
  const managedBy = config.managedBy;
  const hasDescription = config.description !== false;
  const descriptionMin = hasDescription && config.descriptionMinLength ? config.descriptionMinLength : undefined;

  const groups = useMemo(
    () => ledgerGroups(draftGroups, config.groupOf, fieldForPath, state, config.rowLabel),
    [draftGroups, config.groupOf, config.rowLabel, fieldForPath, state],
  );
  const rows = useMemo(() => groups.flatMap((g) => g.rows), [groups]);
  const outcomes = useMemo(
    () => new Map((preview.data?.changes ?? []).map((c) => [c.key, c])),
    [preview.data],
  );
  const { entitySwitch } = editor;
  const switchKey = entitySwitch ? `${entitySwitch.option.key}=${entitySwitch.value}` : '';
  const estimate = useMemo(
    () =>
      catalogEstimate(
        rows,
        config,
        state,
        entitySwitch?.applies ? { [entitySwitch.option.key]: entitySwitch.value } : {},
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, config, state.version, switchKey, entitySwitch?.applies],
  );
  // The ledger group the entity's event switch sits in: its preferred group, else the
  // first one holding a change of that entity.
  const switchGroup = useMemo(() => {
    if (!entitySwitch?.applies) return null;
    const holds = (g: (typeof groups)[number]) => g.rows.some((r) => r.rec.startsWith(`${entitySwitch.entity}:`));
    return (groups.find((g) => g.id === entitySwitch.group && holds(g)) ?? groups.find(holds))?.id ?? null;
  }, [groups, entitySwitch]);
  const block = saveBlock({
    count,
    rows,
    raised,
    errorsFor,
    validateValue,
    invalidInputs,
    formErrors,
    maxChanges: config.maxChanges,
    descriptionLength: description.trim().length,
    descriptionMaxLength: config.descriptionMaxLength,
    ...(descriptionMin ? { descriptionMinLength: descriptionMin } : {}),
  });
  const willRevert = rows.filter((r) => {
    const v = outcomes.get(r.key)?.verdict ?? (r.field ? config.verdictOf(r.rec, r.field, state) : null);
    return v?.verdict === 'overwrites';
  }).length;
  const suspension = config.suspensionOf?.(state) ?? null;

  // ---- save flow ---------------------------------------------------------
  const [phase, setPhase] = useState<Phase>('idle');
  const [confirmLines, setConfirmLines] = useState<string[]>([]);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [expanded, setExpanded] = useState(false);
  const closeAfterSave = useRef(false);
  /** The save in flight: its count and description (the receipt), and what it sent. */
  const saving = useRef<{ count: number; description: string; sent: SavedChange[] }>({
    count: 0,
    description: '',
    sent: [],
  });
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const confirmId = useId();
  const bodyId = useId();

  const onSaved = useCallback(
    (result: RecordApplyResult) => {
      // Before the response re-seeds the baselines: a value the server normalised must
      // not come back as a stale entry (P1-a hand-off). Only what this save sent goes —
      // an edit made while it was in flight stays in the draft.
      // A record kind whose save can leave a change out (a row the server no longer has)
      // says what it stored (`applied`); else the whole body went.
      const { sent: body, ...done } = saving.current;
      const sent = result.applied ?? body;
      // The rows the save created take their real ids in the same, non-undoable step: an
      // undo must never bring a temporary id back (the next save would create it again).
      dispatch({
        type: 'SAVED',
        sent,
        ...(result.created ? { created: { ids: result.created, memberships: config.memberships ?? NO_MEMBERSHIPS } } : {}),
      });
      setReceipt({ result, ...done, ...(result.applied ? { count: result.applied.length } : {}) });
      setFailure(null);
      resetSession();
      setPhase('idle');
      if (closeAfterSave.current) {
        closeAfterSave.current = false;
        exitEdit();
      }
    },
    [dispatch, resetSession, exitEdit, setReceipt, config.memberships],
  );
  const apply = config.useApply({ onSaved });

  const doApply = () => {
    if (!body) return;
    const sent = compileChangeset(state, {
      description,
      ...(body.events ? { events: body.events } : {}),
      ...(body.options ? { options: body.options } : {}),
    });
    saving.current = {
      count,
      description: description.trim(),
      sent: sent.changes.map((c) => ({
        rec: recordKey(c.entity, c.id),
        path: c.action ? actionPath(c.action) : (c.field ?? ''),
        to: c.to,
      })),
    };
    setPhase('idle');
    setFailure(null);
    setApplyFailure(null, null);
    apply.mutate(sent, {
      onError: (e) => {
        closeAfterSave.current = false;
        const drift = asDriftFailure(e);
        if (drift) {
          dispatch({ type: 'REBASE', fresh: freshFromDrift(drift.drifted) });
          setFailure({ kind: 'drift', count: drift.drifted.length, gone: drift.drifted.filter((d) => d.gone).length });
          return;
        }
        const invalid = asValidationFailure(e);
        if (invalid) {
          setApplyFailure(invalid, sent);
          setFailure({ kind: 'validation', message: invalid.message });
          return;
        }
        const refused = asRefusalFailure(e);
        if (refused) {
          setFailure({ kind: 'refused', ...refused });
          return;
        }
        setFailure({ kind: 'error', message: failureMessage(e) });
      },
    });
  };

  const decide = (plan: RecordPreview | null) => {
    const lines = plan
      ? confirmItems(plan, config.confirmFollowUps)
      : ['The preview is unavailable, so what this save triggers could not be checked'];
    if (lines.length === 0) doApply();
    else {
      setConfirmLines(lines);
      setPhase('confirm');
    }
  };
  const latest = useRef({ decide, blocked: block.reason });
  latest.current = { decide, blocked: block.reason };

  const canSave = !block.reason && !apply.isPending && phase === 'idle';
  const requestSave = () => {
    if (!canSave) return;
    if (preview.data) decide(preview.data);
    else if (preview.failed) decide(null);
    else {
      preview.flush();
      setPhase('checking');
    }
  };

  // Save waits for the preview of exactly this draft.
  useEffect(() => {
    if (phase !== 'checking') return;
    if (latest.current.blocked) setPhase('idle');
    else if (preview.data) latest.current.decide(preview.data);
    else if (preview.failed) latest.current.decide(null);
    else if (!preview.pending) setPhase('idle'); // refused (400): the errors are on the fields
  }, [phase, preview.data, preview.failed, preview.pending]);

  // A confirmation is for the plan it showed; any edit voids it.
  const bodyKey = JSON.stringify(body);
  const confirmedKey = useRef(bodyKey);
  useEffect(() => {
    if (phase === 'confirm' && confirmedKey.current !== bodyKey) setPhase('idle');
    confirmedKey.current = bodyKey;
  }, [bodyKey, phase]);

  // The receipt gives way to the next draft.
  useEffect(() => {
    if (count > 0 && receipt) setReceipt(null);
  }, [count, receipt, setReceipt]);

  // The close prompt and errors need the full sheet.
  useEffect(() => {
    if (closePrompt || failure) setExpanded(true);
  }, [closePrompt, failure]);

  useRecordShortcuts(
    {
      ...(history.canUndo ? { onUndo: history.undo } : {}),
      ...(history.canRedo ? { onRedo: history.redo } : {}),
      ...(canSave && count > 0 ? { onSave: requestSave } : {}),
      onClose: phase === 'confirm' ? () => setPhase('idle') : closePrompt ? cancelClose : requestClose,
    },
    { enabled: shortcuts && !viewOnly },
  );

  const sheet = variant === 'sheet';
  const open = !sheet || expanded;
  const saveLabel = apply.isPending
    ? 'Saving…'
    : phase === 'checking'
      ? 'Checking what it does…'
      : `Save ${changesText(count)}`;

  const saveButton = (compact: boolean) => (
    <Button
      ref={saveButtonRef}
      variant="contained"
      fullWidth={!compact}
      size={compact ? 'small' : 'medium'}
      disabled={!canSave || count === 0}
      onClick={requestSave}
      data-save-button
    >
      {saveLabel}
      {!compact && <Kbd>⌘S</Kbd>}
    </Button>
  );

  // ---- header ------------------------------------------------------------
  const title = viewOnly ? (
    <Typography variant="subtitle2" component="h2" sx={{ m: 0 }}>
      {viewTitle}
    </Typography>
  ) : (
    <Typography variant="subtitle2" component="h2" sx={{ m: 0 }} aria-live="polite">
      Draft{' '}
      <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
        · {changesText(count)}
      </Box>
    </Typography>
  );
  const header = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        pl: sheet ? 0.5 : 1.75,
        pr: 0.75,
        py: 0.75,
        borderBottom: open ? 1 : 0,
        borderColor: 'divider',
        flex: 'none',
      }}
    >
      {sheet && !viewOnly ? (
        <ButtonBase
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-controls={bodyId}
          sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, '&.Mui-focusVisible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` } }}
        >
          {expanded ? <ExpandMoreIcon fontSize="small" aria-hidden /> : <ExpandLessIcon fontSize="small" aria-hidden />}
          {title}
        </ButtonBase>
      ) : (
        title
      )}
      <Box sx={{ flex: 1 }} />
      {viewOnly ? (
        // The undo / redo / close row's height, kept: E swaps the buttons in, nothing moves.
        <Box aria-hidden data-ledger-header-room sx={{ height: 30, width: 0 }} />
      ) : (
        <>
          <Tooltip title={history.undoLabel ? `Undo ${history.undoLabel} (⌘Z)` : 'Nothing to undo'}>
            <span>
              <IconButton
                size="small"
                disabled={!history.canUndo}
                onClick={history.undo}
                aria-label={history.undoLabel ? `Undo ${history.undoLabel}` : 'Undo'}
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={history.redoLabel ? `Redo ${history.redoLabel} (⇧⌘Z)` : 'Nothing to redo'}>
            <span>
              <IconButton
                size="small"
                disabled={!history.canRedo}
                onClick={history.redo}
                aria-label={history.redoLabel ? `Redo ${history.redoLabel}` : 'Redo'}
              >
                <RedoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {sheet && !expanded && count > 0 && <Box sx={{ ml: 0.5 }}>{saveButton(true)}</Box>}
          <Tooltip title="Close editor (Esc)">
            <IconButton size="small" onClick={requestClose} aria-label="Close editor">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );

  // ---- body --------------------------------------------------------------
  const errorRows = rows.filter((r) => errorsFor(r.rec, r.path).length > 0);
  const banner = managedBy ? (
    suspension ? (
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon fontSize="inherit" />}
        sx={DENSE_ALERT_SX}
        data-restech-banner={suspension.queued ? 'suspended-queued' : 'suspended'}
      >
        {suspension.text}
        {/* A sync past the pause can still overwrite — the count stays in view. */}
        {willRevert > 0 && (
          <Box component="span" sx={{ display: 'block', mt: 0.25, fontWeight: 600 }}>
            {willRevert} of your changes will be overwritten by {managedBy}.
          </Box>
        )}
      </Alert>
    ) : (
      <Alert
        severity={willRevert > 0 ? 'warning' : 'info'}
        icon={<SyncIcon fontSize="inherit" />}
        sx={DENSE_ALERT_SX}
        data-restech-banner={willRevert > 0 ? 'overwrites' : 'managed'}
      >
        <b>
          {managedBy} manages this {config.recordNoun}
        </b>
        {willRevert > 0
          ? ` · ${willRevert} of your changes will be overwritten by its next ${config.feedEvent ?? 'update'}.`
          : ` · ${config.managedNote ?? 'fields it controls carry the sync icon; edits are allowed'}.`}
        {config.restechHelpStory && (
          <>
            {' '}
            <Link component={RouterLink} to={helpStoryPath(config.restechHelpStory)} target="_blank" rel="noopener noreferrer">
              Change it in {managedBy} instead?
            </Link>
          </>
        )}
        {willRevert > 0 && config.managedNote && (
          <Box component="span" data-managed-note sx={{ display: 'block', mt: 0.25 }}>
            {config.managedNote}.
          </Box>
        )}
      </Alert>
    )
  ) : null;

  const failureAlert = failure ? (
    failure.kind === 'drift' ? (
      <Alert severity="warning" icon={<SyncProblemIcon fontSize="inherit" />} sx={DENSE_ALERT_SX} data-failure="drift">
        Nothing was saved: {failure.count} of your changes {failure.count === 1 ? 'was' : 'were'} changed on the
        server while you edited.{' '}
        {failure.gone === failure.count
          ? `${failure.gone === 1 ? 'It is' : 'They are'} on something deleted elsewhere — drop ${failure.gone === 1 ? 'it' : 'them'}, then save again.`
          : failure.gone > 0
            ? `${failure.gone} of them ${failure.gone === 1 ? 'is' : 'are'} on something deleted elsewhere — drop ${failure.gone === 1 ? 'it' : 'those'}; choose Keep mine or Take theirs on the others, then save again.`
            : 'Choose Keep mine or Take theirs on each, then save again.'}
      </Alert>
    ) : failure.kind === 'refused' ? (
      <Alert severity="info" sx={DENSE_ALERT_SX} data-failure="refused">
        Nothing was saved: {failure.message}
        {failure.links.map((l) => (
          <Box component="span" key={l.to} sx={{ display: 'block', mt: 0.25 }}>
            <Link component={RouterLink} to={l.to}>
              {l.label}
            </Link>
          </Box>
        ))}
      </Alert>
    ) : (
      <Alert
        severity="error"
        icon={<ErrorOutlineIcon fontSize="inherit" />}
        sx={DENSE_ALERT_SX}
        data-failure={failure.kind}
        {...(failure.kind === 'error'
          ? {
              action: (
                <Button color="inherit" size="small" onClick={requestSave} sx={{ py: 0 }}>
                  Try again
                </Button>
              ),
            }
          : {})}
      >
        Nothing was saved: {failure.message}
      </Alert>
    )
  ) : null;

  const refused =
    formErrors.length > 0 || (failure?.kind === 'validation' && errorRows.length > 0) ? (
      <Box component="ul" data-error-summary sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.25 }}>
        {formErrors.map((e) => (
          <Typography component="li" key={`${e.key}|${e.message}`} variant="caption" color="error" sx={{ display: 'flex', gap: 0.5 }}>
            <ErrorOutlineIcon aria-hidden sx={{ fontSize: 14, mt: '2px' }} />
            {e.message}
          </Typography>
        ))}
        {errorRows.map((r) => (
          <Box component="li" key={r.key}>
            <Link
              component="button"
              type="button"
              variant="caption"
              color="error"
              onClick={() => focusField(r.rec, r.path)}
              sx={{ textAlign: 'left' }}
            >
              {r.label ?? r.field?.label ?? r.path}: {errorsFor(r.rec, r.path).join(' · ')}
            </Link>
          </Box>
        ))}
      </Box>
    ) : null;

  const descriptionSection = (
    <Section title="Description">
      <TextField
        multiline
        minRows={descriptionMin ? 1 : 2}
        maxRows={descriptionMin ? 4 : 6}
        fullWidth
        size="small"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={descriptionMin ? `Required — at least ${descriptionMin} characters` : 'Optional'}
        inputProps={{
          'aria-label': 'Description',
          ...(descriptionMin ? { 'aria-required': true } : {}),
          ...(config.descriptionMaxLength ? { maxLength: config.descriptionMaxLength } : {}),
        }}
        data-ledger-description={descriptionMin ? 'required' : 'optional'}
        helperText={
          config.descriptionMaxLength && description.length > config.descriptionMaxLength * 0.8
            ? `What and why — recorded in the audit log and the change history · ${description.length} / ${config.descriptionMaxLength}`
            : 'What and why — recorded in the audit log and the change history'
        }
      />
    </Section>
  );

  const content =
    count === 0 ? (
      <>
        {receipt ? (
          <LedgerReceipt
            receipt={receipt}
            timezone={config.timezone}
            eventStatusLabel={config.eventStatusLabel}
            deliveriesHref={config.deliveriesHref}
            onClose={exitEdit}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" data-ledger-empty sx={{ py: 1 }}>
            Edit any field — every change lands here with what it triggers. Nothing is written until you save.
            <Box component="span" sx={{ display: 'block', mt: 1, typography: 'caption' }}>
              ⌘Z / ⇧⌘Z undo and redo · ⌘S save · Esc close
            </Box>
          </Typography>
        )}
        {banner}
      </>
    ) : (
      <>
        {banner}
        {failureAlert}
        {refused}
        <Section
          title="Changes"
          action={
            <Tooltip title="Discard every change — ⌘Z brings them back" describeChild>
              <Button size="small" onClick={() => dispatch({ type: 'DISCARD_ALL' })} sx={{ py: 0, minHeight: 24 }}>
                Discard all
              </Button>
            </Tooltip>
          }
        >
          <LedgerChanges groups={groups} outcomes={outcomes} switchGroup={switchGroup} />
        </Section>
        {raised.length > 0 && (
          <Section title={block.missing.length > 0 ? 'Choices needed' : 'Choices'}>
            <Box sx={{ display: 'grid', gap: 1 }}>
              {raised.map((c) => (
                <ChoiceQuestion key={c.option.key} choice={c} />
              ))}
            </Box>
          </Section>
        )}
        <Section title="On save">
          <LedgerOnSave estimate={estimate} />
        </Section>
        {hasDescription && !descriptionMin && descriptionSection}
      </>
    );

  // ---- footer ------------------------------------------------------------
  const footer =
    viewOnly || (count === 0 && !closePrompt) ? null : (
      <Box sx={{ borderTop: 1, borderColor: 'divider', px: 1.75, py: 1.25, display: 'grid', gap: 0.75, flex: 'none' }}>
        {closePrompt ? (
          <Box role="group" aria-labelledby={`${confirmId}-close`} data-close-prompt sx={{ display: 'grid', gap: 0.5 }}>
            <Typography id={`${confirmId}-close`} variant="body2" sx={{ fontWeight: 600 }}>
              {changesText(count)} unsaved
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Closing the editor discards them; ⌘Z brings them back while this page stays open.
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button size="small" autoFocus onClick={cancelClose}>
                Keep editing
              </Button>
              <Button
                size="small"
                color="error"
                onClick={() => {
                  dispatch({ type: 'DISCARD_ALL' });
                  exitEdit();
                }}
              >
                Discard
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={!canSave}
                onClick={() => {
                  closeAfterSave.current = true;
                  cancelClose();
                  requestSave();
                }}
              >
                Save
              </Button>
            </Box>
            {block.reason && (
              <Typography variant="caption" sx={{ display: 'flex', gap: 0.5 }}>
                <WarningAmberIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main', mt: '2px' }} />
                {block.reason}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            {descriptionMin && descriptionSection}
            {block.reason && (
              <Typography variant="caption" data-block-reason sx={{ display: 'flex', gap: 0.5 }}>
                <WarningAmberIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main', mt: '2px' }} />
                {block.reason}
              </Typography>
            )}
            {saveButton(false)}
            {persistence === 'too_large' && (
              <Typography variant="caption" color="text.secondary">
                This draft is too large to survive a reload.
              </Typography>
            )}
            {persistence === 'unavailable' && (
              <Typography variant="caption" color="text.secondary">
                This browser blocks session storage — a reload loses this draft.
              </Typography>
            )}
          </>
        )}
      </Box>
    );

  return (
    <Box
      component="aside"
      aria-label={viewOnly ? viewTitle : 'Draft'}
      data-ledger={variant}
      data-ledger-mode={mode}
      data-expanded={sheet ? expanded : undefined}
      sx={[
        {
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          overflow: 'hidden',
          minHeight: 0,
        },
        sheet
          ? {
              borderRadius: '8px 8px 0 0',
              boxShadow: 4,
              maxHeight: expanded ? '60vh' : 'none',
            }
          : { border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: '100%' },
      ]}
    >
      {header}
      {open && tabs && (
        <Box data-ledger-tabs sx={{ px: 1.75, pt: 1, flex: 'none' }}>
          {tabs}
        </Box>
      )}
      {open && (
        <>
          <Box
            id={bodyId}
            sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', px: 1.75, py: 1.25, display: 'grid', gap: 1.75, alignContent: 'start' }}
          >
            {segmentBody !== undefined || viewOnly ? (
              segmentBody
            ) : (
              <>
                {restored && count > 0 && (
                  <Alert
                    severity="info"
                    sx={DENSE_ALERT_SX}
                    data-restored
                    action={
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {restoredDiscard && (
                          <Button color="inherit" size="small" onClick={discardRestored} sx={{ py: 0 }}>
                            Discard
                          </Button>
                        )}
                        <Button color="inherit" size="small" onClick={dismissRestored} sx={{ py: 0 }}>
                          Keep
                        </Button>
                      </Box>
                    }
                  >
                    <Box component="span" sx={restoredDiscard ? undefined : { whiteSpace: 'nowrap' }}>
                      Restored {changesText(restored.count)} · {formatZoned(restored.savedAt, config.timezone, { weekday: false })}
                    </Box>
                  </Alert>
                )}
                {content}
              </>
            )}
          </Box>
          {footer}
        </>
      )}
      <Popover
        open={phase === 'confirm'}
        anchorEl={saveButtonRef.current}
        onClose={() => setPhase('idle')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          paper: {
            role: 'alertdialog',
            'aria-labelledby': confirmId,
            sx: { p: 1.5, mb: 1, maxWidth: 340, display: 'grid', gap: 1 },
          } as object,
        }}
      >
        <Typography id={confirmId} variant="body2">
          {confirmLines.join(' · ')} — save?
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => setPhase('idle')}>
            Back
          </Button>
          <Button size="small" variant="contained" autoFocus onClick={doApply} data-confirm-save>
            Save
            <Kbd>↵</Kbd>
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}
